import { Booking, BookingListParams, BookingListResult, ContactSummary, EventType, PaymentSummary, PromoSettings } from "./types";

const isLocalBrowser = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const isAdminDomain = window.location.hostname === "admin.picshot.online";
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (isLocalBrowser ? "http://127.0.0.1:4000/api" : isAdminDomain ? "https://booking.picshot.online/api" : "/api");
const ADMIN_TOKEN_KEY = "picshot_admin_token";

export function getAdminToken() {
  return window.localStorage.getItem(ADMIN_TOKEN_KEY) || "";
}

export function setAdminToken(token: string) {
  window.localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken() {
  window.localStorage.removeItem(ADMIN_TOKEN_KEY);
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getAdminToken();
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    cache: "no-store",
    ...options,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || "API request failed");
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export const api = {
  loginAdmin: (password: string) =>
    request<{ token: string }>("/auth/login", { method: "POST", body: JSON.stringify({ password }) }),
  getAdminSession: () => request<{ ok: boolean }>("/auth/session"),
  getPromo: () => request<PromoSettings>("/promo"),
  updatePromo: (promo: Partial<PromoSettings>) =>
    request<PromoSettings>("/promo", { method: "PATCH", body: JSON.stringify(promo) }),
  getEvents: (includeDisabled = false) => request<EventType[]>(`/events?includeDisabled=${includeDisabled}`),
  getEvent: (id: string) => request<EventType>(`/events/${id}`),
  createEvent: (event: Partial<EventType>) =>
    request<EventType>("/events", { method: "POST", body: JSON.stringify(event) }),
  updateEvent: (id: string, event: Partial<EventType>) =>
    request<EventType>(`/events/${id}`, { method: "PATCH", body: JSON.stringify(event) }),
  deleteEvent: (id: string) => request<void>(`/events/${id}`, { method: "DELETE" }),
  getAvailability: (id: string, month: string) =>
    request<Record<string, string[]>>(`/events/${id}/availability?month=${month}`),
  getAdminAvailability: (id: string, month: string) =>
    request<Record<string, string[]>>(`/events/${id}/availability/admin?month=${month}`),
  createBooking: (booking: Partial<Booking>) =>
    request<Booking>("/bookings", { method: "POST", body: JSON.stringify(booking) }),
  createManualBooking: (booking: Partial<Booking>) =>
    request<Booking>("/bookings/manual", { method: "POST", body: JSON.stringify(booking) }),
  getBookings: () => request<Booking[]>("/bookings"),
  getBookingsPage: (params: BookingListParams) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") query.set(key, String(value));
    });
    return request<BookingListResult>(`/bookings?${query.toString()}`);
  },
  getContacts: () => request<ContactSummary[]>("/contacts"),
  getContact: (id: string) => request<ContactSummary>(`/contacts/${encodeURIComponent(id)}`),
  updateBooking: (id: string, booking: Partial<Booking>) =>
    request<Booking>(`/bookings/${id}`, { method: "PATCH", body: JSON.stringify(booking) }),
  deleteBooking: (id: string) => request<void>(`/bookings/${id}`, { method: "DELETE" }),
  getPayment: (bookingId: string) => request<PaymentSummary>(`/payments/${bookingId}`),
  createQPayInvoice: (bookingId: string) =>
    request<PaymentSummary>(`/payments/qpay/invoice/${bookingId}`, { method: "POST" }),
  checkQPayPayment: (bookingId: string) =>
    request<PaymentSummary>(`/payments/qpay/check/${bookingId}`, { method: "POST" }),
};
